import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import type { AdminFaqResponse, FaqQuestionStats } from '../api/types';

/**
 * Perguntas e respostas que o cliente vê na tela de dúvidas.
 *
 * Cada linha traz o retorno de quem já a abriu: quantas vezes resolveu e
 * quantas acabaram no WhatsApp. É esse número que diz qual resposta reescrever
 * — uma pergunta muito aberta e pouco resolvida está custando atendimento.
 */
export function FaqPanel() {
  const [data, setData] = useState<AdminFaqResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [editando, setEditando] = useState<number | 'nova' | null>(null);

  const carregar = useCallback(async () => {
    try {
      setData(await api.adminFaq());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar as perguntas.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function agir(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      setOk(mensagem);
      setEditando(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  /** Move uma pergunta e reenvia a ordem inteira, já na posição final. */
  function mover(indice: number, direcao: -1 | 1) {
    if (!data) return;
    const ids = data.items.map((i) => i.id);
    const destino = indice + direcao;
    if (destino < 0 || destino >= ids.length) return;
    [ids[indice], ids[destino]] = [ids[destino]!, ids[indice]!];
    void agir(() => api.adminReordenarFaq(ids), 'Ordem atualizada.');
  }

  function apagar(q: FaqQuestionStats) {
    if (
      !window.confirm(
        q.aberturas > 0
          ? `Apagar "${q.question}"? Ela já foi aberta ${q.aberturas} vezes e o histórico dessas sessões perde o vínculo. Desativar costuma ser melhor.`
          : `Apagar "${q.question}"?`,
      )
    ) {
      return;
    }
    void agir(() => api.adminApagarPergunta(q.id), 'Pergunta apagada.');
  }

  if (erro && !data) return <Banner kind="error">{erro}</Banner>;
  if (!data) return <Spinner label="Carregando as perguntas…" />;

  return (
    <div className="panel">
      <h2>Dúvidas dos clientes</h2>
      <p className="muted">
        O cliente escolhe uma destas perguntas e recebe a resposta na hora. Quando nenhuma resolve, ele é levado ao
        WhatsApp cadastrado na aba <b>Pedidos</b>, com a dúvida já escrita na mensagem.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <div className="stat-row">
        <span>
          <b>{data.totals.ativas}</b> perguntas no ar
        </span>
        <span className="dim">
          <b>{data.totals.perguntas}</b> cadastradas
        </span>
        <span>
          <b>{data.totals.aberturas}</b> consultas
        </span>
        <span className={data.totals.atendimentos ? 'warn' : ''}>
          <b>{data.totals.atendimentos}</b> foram para o WhatsApp
        </span>
      </div>

      <div className="actions-row">
        <button className="btn primary" onClick={() => setEditando(editando === 'nova' ? null : 'nova')}>
          {editando === 'nova' ? 'Cancelar' : '+ Nova pergunta'}
        </button>
      </div>

      {editando === 'nova' && (
        <Formulario
          ocupado={ocupado}
          onSalvar={(dados) => agir(() => api.adminCriarPergunta(dados), 'Pergunta criada.')}
          onCancelar={() => setEditando(null)}
        />
      )}

      {data.items.length === 0 ? (
        <p className="muted">
          Nenhuma pergunta cadastrada ainda — a tela do cliente vai oferecer só o atendimento pelo WhatsApp.
        </p>
      ) : (
        <ul className="faq-lista">
          {data.items.map((q, i) => (
            <li key={q.id} className={`faq-item ${q.active ? '' : 'off'}`}>
              {editando === q.id ? (
                <Formulario
                  inicial={q}
                  ocupado={ocupado}
                  onSalvar={(dados) => agir(() => api.adminSalvarPergunta(q.id, dados), 'Pergunta salva.')}
                  onCancelar={() => setEditando(null)}
                />
              ) : (
                <>
                  <div className="faq-item-topo">
                    <b>{q.question}</b>
                    {!q.active && <span className="faq-off">fora do ar</span>}
                  </div>
                  <p className="faq-resposta">{q.answer}</p>

                  <div className="faq-numeros dim">
                    <span>
                      <b>{q.aberturas}</b> consultas
                    </span>
                    <span className="pos">{q.resolvidas} resolveram</span>
                    <span>{q.naoResolvidas} não resolveram</span>
                    <span className={q.atendimentos ? 'warn' : ''}>{q.atendimentos} foram ao WhatsApp</span>
                  </div>

                  <div className="actions-row">
                    <button className="btn ghost small" onClick={() => setEditando(q.id)}>
                      Editar
                    </button>
                    <button
                      className="btn ghost small"
                      disabled={ocupado}
                      onClick={() =>
                        void agir(
                          () => api.adminSalvarPergunta(q.id, { active: !q.active }),
                          q.active ? 'Pergunta tirada do ar.' : 'Pergunta no ar.',
                        )
                      }
                    >
                      {q.active ? 'Tirar do ar' : 'Publicar'}
                    </button>
                    <button className="btn ghost small" disabled={ocupado || i === 0} onClick={() => mover(i, -1)}>
                      ↑
                    </button>
                    <button
                      className="btn ghost small"
                      disabled={ocupado || i === data.items.length - 1}
                      onClick={() => mover(i, 1)}
                    >
                      ↓
                    </button>
                    <button className="btn ghost small" disabled={ocupado} onClick={() => apagar(q)}>
                      Apagar
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Formulario({
  inicial,
  ocupado,
  onSalvar,
  onCancelar,
}: {
  inicial?: { question: string; answer: string };
  ocupado: boolean;
  onSalvar: (dados: { question: string; answer: string }) => void;
  onCancelar: () => void;
}) {
  const [question, setQuestion] = useState(inicial?.question ?? '');
  const [answer, setAnswer] = useState(inicial?.answer ?? '');
  const pronto = question.trim().length > 0 && answer.trim().length > 0;

  return (
    <div className="faq-form form">
      <label className="field">
        <span>
          Pergunta <span className="req">obrigatório</span>
        </span>
        <input
          maxLength={200}
          placeholder="Ex.: Em quanto tempo a skin chega no meu inventário?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <small>Escreva do jeito que o cliente perguntaria. É o texto que ele vê na lista.</small>
      </label>

      <label className="field">
        <span>
          Resposta <span className="req">obrigatório</span>
        </span>
        <textarea
          rows={5}
          maxLength={4000}
          placeholder="A resposta que o atendimento daria. Pode usar parágrafos."
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
        />
        <small>{answer.length}/4000</small>
      </label>

      <div className="actions-row">
        <button className="btn primary" disabled={!pronto || ocupado} onClick={() => onSalvar({ question, answer })}>
          {ocupado ? 'Salvando…' : 'Salvar'}
        </button>
        <button className="btn ghost" onClick={onCancelar} disabled={ocupado}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
